'use client';

import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import { fetchRemoteConfig, REMOTE_CONFIG_DEFAULTS, RemoteConfigValues, setFirebaseConfig } from './firebase-client';
import { FirebaseConfig } from './firebase-config';

import { setExperimentUserProperties } from '@src/lib/analytics/firebase-analytics';

type FirebaseContextType = {
	config: FirebaseConfig;
	remoteConfig: RemoteConfigValues;
	remoteConfigReady: boolean;
} | null;

const FirebaseContext = createContext<FirebaseContextType>(null);

export const FirebaseProvider = ({ config, children }: { config: FirebaseConfig; children: ReactNode }) => {
	const [remoteConfig, setRemoteConfig] = useState<RemoteConfigValues>(REMOTE_CONFIG_DEFAULTS);
	const [remoteConfigReady, setRemoteConfigReady] = useState(false);

	useEffect(() => {
		if (!config?.projectId) {
			// No valid SSR config: mark ready so consumers stop waiting and fall back to defaults.
			setRemoteConfigReady(true);
			return;
		}
		setFirebaseConfig(config);

		let cancelled = false;
		fetchRemoteConfig().then((values) => {
			if (!cancelled) {
				setRemoteConfig(values);
				setRemoteConfigReady(true);
				void setExperimentUserProperties({
					nc_phone_verification_variant: String(values.nc_phone_verification),
				});
			}
		});

		return () => {
			cancelled = true;
		};
	}, [config]);

	const value = useMemo(
		() => ({ config, remoteConfig, remoteConfigReady }),
		[config, remoteConfig, remoteConfigReady]
	);

	return <FirebaseContext.Provider value={value}>{children}</FirebaseContext.Provider>;
};

export const useFirebaseConfig = (): FirebaseConfig => {
	const ctx = useContext(FirebaseContext);
	if (!ctx) throw new Error('useFirebaseConfig must be used within a FirebaseProvider');
	return ctx.config;
};

export const useRemoteConfig = () => {
	const ctx = useContext(FirebaseContext);
	if (!ctx) {
		return { values: REMOTE_CONFIG_DEFAULTS, ready: false };
	}
	return { values: ctx.remoteConfig, ready: ctx.remoteConfigReady };
};
